import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useMedplum } from "@medplum/react";

import { EncounterContext } from "@/contexts/encounter";
import { ENCOUNTER_BILLING_TAB_SECTION } from "@/encounters/constants/unsaved-encounters";
import {
  BillingSectionSaveOutcome,
  useRegisterBillingSectionSave,
} from "@/encounters/contexts/BillingSectionSaveContext";
import { saveBillerSectionChargeEdit } from "@/encounters/services/biller-section-charge-edit";
import { saveBillerSectionProcedures } from "@/encounters/services/biller-section-procedures";
import { voidEncounterChargeItem } from "@/encounters/services/encounter-charge-items";
import { EncounterChargeItemRow } from "@/encounters/types/encounter-charge-item-rows";
import { StagedProceduresOperation } from "@/encounters/types/procedures";
import { reportPlaceOfServiceRefreshFailure } from "@/encounters/utils/report-place-of-service-refresh-failure";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";
import { getActiveOrganizationId } from "@/utils/medplum";
import { toast } from "@/utils/notifications";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import {
  applyStagedChargeEdits,
  StagedBillerSectionChargeEdit,
} from "../utils/staged-biller-section-edits";
import {
  buildStagedBillerSectionRows,
  dropStagedProcedureCode,
  replaceStagedProcedure,
} from "../utils/staged-biller-section-rows";

type UseStagedBillerSectionProceduresParams = {
  clientId: string | undefined;
  encounterId: string | undefined;
  /** Asks for the encounter's charges to be read again, so a saved entry becomes a real row. */
  reloadRows: () => void;
};

type UseStagedBillerSectionProcedures = {
  /** The staged entries as the rows they will become, to list under the captured ones. */
  stagedRows: EncounterChargeItemRow[];
  hasStagedChanges: boolean;
  stageOperation: (operation: StagedProceduresOperation) => void;
  /**
   * Captured charges the biller has deleted, by ChargeItem id, waiting for Save Billing Info to void
   * them. The section stops listing them as soon as they are staged.
   */
  stagedDeletionIds: string[];
  stageDeletion: (chargeItemId: string) => void;
  /**
   * Takes a code back off the staged entries. What deleting a row that was never written amounts to:
   * there is no charge to void, so it comes off the staged set instead of onto the deletion list.
   */
  unstageProcedureCode: (procedureCode: string) => void;
  /** Applies the biller's unsaved edits to the captured rows, which is what the table renders. */
  previewChargeEdits: (rows: EncounterChargeItemRow[]) => EncounterChargeItemRow[];
  /**
   * Holds an edit the biller finished on a captured row. Replaces any edit already staged for that
   * charge, because only one of them can be written and the newer session is the biller's answer.
   */
  stageChargeEdit: (chargeItemId: string, operation: StagedProceduresOperation) => void;
  /**
   * Re-stages one code that is not written yet, under the diagnoses the biller just chose. What
   * editing a staged row amounts to: there is no charge to update, so the session replaces the entry.
   */
  restageProcedure: (procedureCode: string, operation: StagedProceduresOperation) => void;
};

/** What the save just did, so the toast says which way the billing went rather than guessing. */
const describeSavedWork = (additions: number, deletions: number, edits: number): string => {
  const kinds = [additions, deletions, edits].filter(Boolean).length;

  if (kinds > 1) return "This encounter's billing was updated.";
  if (additions) return "The procedures were added to this encounter's billing.";
  if (deletions) return "The procedure codes were removed from this encounter's billing.";

  return "The procedure codes were updated on this encounter's billing.";
};

/**
 * Owns the biller section's staged work: what the picker has added, what the biller has deleted, and
 * the writes that land when the billing tab is saved.
 *
 * Nothing the biller does in the section reaches the server on its own. Additions, deletions and
 * edits are all staged here, and Save Billing Info writes them. That save arrives through the
 * billing tab's save coordinator. One hook holds all three, because the encounter's navigation
 * blocker takes one answer per section and separate hooks would each register the same key.
 */
function useStagedBillerSectionProcedures({
  clientId,
  encounterId,
  reloadRows,
}: UseStagedBillerSectionProceduresParams): UseStagedBillerSectionProcedures {
  const medplum = useMedplum();
  const { hasBillerRole } = usePractitionerPermissions();
  const { registerUnsavedCheck, unregisterUnsavedCheck } = useContext(EncounterContext);
  // The practice whose fee definition the charges are priced against, read here rather than in the
  // service for the same reason the sign-and-lock capture does: it belongs to the signed-in session.
  const practiceOrganizationId = getActiveOrganizationId();

  const [operations, setOperations] = useState<StagedProceduresOperation[]>([]);
  const [stagedDeletionIds, setStagedDeletionIds] = useState<string[]>([]);
  const [chargeEdits, setChargeEdits] = useState<StagedBillerSectionChargeEdit[]>([]);

  const hasStagedChanges =
    operations.length > 0 || stagedDeletionIds.length > 0 || chargeEdits.length > 0;
  const stagedRows = useMemo(() => buildStagedBillerSectionRows(operations), [operations]);

  const stageOperation = useCallback((operation: StagedProceduresOperation) => {
    setOperations((current) => [...current, operation]);
  }, []);

  const previewChargeEdits = useCallback(
    (rows: EncounterChargeItemRow[]) => applyStagedChargeEdits(rows, chargeEdits),
    [chargeEdits],
  );

  const stageChargeEdit = useCallback(
    (chargeItemId: string, operation: StagedProceduresOperation) => {
      setChargeEdits((current) => [
        ...current.filter((edit) => edit.chargeItemId !== chargeItemId),
        { chargeItemId, operation },
      ]);
    },
    [],
  );

  const restageProcedure = useCallback(
    (procedureCode: string, operation: StagedProceduresOperation) => {
      setOperations((current) =>
        replaceStagedProcedure(current, procedureCode, operation.formValues),
      );
    },
    [],
  );

  // Deduplicated: deleting the same row twice is one void, and staging it twice would report a second
  // deletion the biller never made.
  //
  // Any edit staged for that charge is dropped with it. The row is gone from the table, so writing an
  // edit to it would update a charge the same save is about to void.
  const stageDeletion = useCallback((chargeItemId: string) => {
    setChargeEdits((current) => current.filter((edit) => edit.chargeItemId !== chargeItemId));
    setStagedDeletionIds((current) =>
      current.includes(chargeItemId) ? current : [...current, chargeItemId],
    );
  }, []);

  const unstageProcedureCode = useCallback((procedureCode: string) => {
    setOperations((current) => dropStagedProcedureCode(current, procedureCode));
  }, []);

  // Save Billing Info lands here.
  //
  // Written one at a time, oldest first: each write reconciles against what the one before it left
  // behind, the same order the table previewed them in. Each is dropped as it lands, so an entry
  // staged while the save was in flight survives it.
  //
  // A failure stops the run and leaves everything from it staged, including an entry whose resources
  // were written but whose charges were not: saving again re-runs it, which adds only what is
  // missing, and leaving it staged is what gets the biller to try. Edits and then deletions run after
  // the additions for the same reason they stop when one fails. A biller told the save went wrong
  // should find every change they made still in front of them, not some of it applied.
  //
  // Not a useCallback: useRegisterBillingSectionSave keeps the latest render's handler, so this
  // always reads the current staged set.
  const saveStagedOperations = async (): Promise<BillingSectionSaveOutcome> => {
    const savedAdditionCount = operations.length;
    const savedDeletionCount = stagedDeletionIds.length;
    const savedEditCount = chargeEdits.length;

    if (
      (!savedAdditionCount && !savedDeletionCount && !savedEditCount) ||
      !clientId ||
      !encounterId
    ) {
      return "nothing-to-write";
    }

    let hasFailed = false;

    for (const operation of operations) {
      const result = await saveBillerSectionProcedures({
        medplum,
        clientId,
        encounterId,
        operation,
        isBiller: hasBillerRole,
        practiceOrganizationId,
      });

      if (result.placeOfServiceRefreshFailure) {
        reportPlaceOfServiceRefreshFailure(encounterId, result.placeOfServiceRefreshFailure);
      }

      if (!result.ok) {
        // Only the encounter's id and the reason reach Sentry: what was billed for is clinical, and a
        // failure to record it is an operational problem.
        captureExceptionWithoutPhi(
          "Failed to save the billing tab's staged procedures",
          new Error(result.details ? `${result.reason}: ${result.details}` : result.reason),
          { encounterId },
        );
        hasFailed = true;
        break;
      }

      setOperations((current) => current.filter(({ id }) => id !== operation.id));
    }

    // The diagnoses and notes a biller changed are written as the biller section's own, and the
    // units and modifiers go on the charge. The Procedure the charge was captured from is left as
    // the provider recorded it — see saveBillerSectionChargeEdit.
    for (const edit of hasFailed ? [] : chargeEdits) {
      const result = await saveBillerSectionChargeEdit({
        medplum,
        clientId,
        encounterId,
        isBiller: hasBillerRole,
        operation: edit.operation,
        chargeItemId: edit.chargeItemId,
      });

      if (!result.ok) {
        // The charge's own id is not clinical, and which charge failed to update is what ops needs.
        captureExceptionWithoutPhi(
          "Failed to save an edit made on the billing tab",
          new Error(result.details ? `${result.reason}: ${result.details}` : result.reason),
          { encounterId, chargeItemId: edit.chargeItemId },
        );
        hasFailed = true;
        break;
      }

      // Matched on the picker session as well as the charge, so an edit the biller staged for the
      // same row while the save was running stays for the next one. Replacing it here would drop a
      // change they made and can still see in the table.
      setChargeEdits((current) =>
        current.filter(
          (staged) =>
            staged.chargeItemId !== edit.chargeItemId || staged.operation.id !== edit.operation.id,
        ),
      );
    }

    // Only the charge is voided. The Procedure and Condition it was captured from stay exactly as they
    // are — see voidEncounterChargeItem — so the encounter's clinical record is untouched by a biller
    // deciding not to bill for something.
    for (const chargeItemId of hasFailed ? [] : stagedDeletionIds) {
      const result = await voidEncounterChargeItem({ medplum, chargeItemId });

      if (!result.ok) {
        // The charge's own id is not clinical, and which charge failed to void is what ops needs.
        captureExceptionWithoutPhi(
          "Failed to void a charge deleted from the billing tab",
          new Error(result.details ? `${result.reason}: ${result.details}` : result.reason),
          { encounterId, chargeItemId },
        );
        hasFailed = true;
        break;
      }

      setStagedDeletionIds((current) => current.filter((id) => id !== chargeItemId));
    }

    // Either way: a run that failed part-way still wrote the entries that landed, and the table has
    // to show them as the charges they now are. The re-read is not awaited — it is a request the
    // reading hook serves, and the toast below reports the writes, not the redisplay.
    reloadRows();

    if (hasFailed) {
      toast({
        title: "Failed to save the billing codes",
        message:
          "Your changes are still here — try saving again, or contact support if the error persists.",
        type: "error",
        autoClose: 5000,
      });

      return "failed";
    }

    toast({
      title: "Billing codes saved",
      message: describeSavedWork(savedAdditionCount, savedDeletionCount, savedEditCount),
      type: "success",
      autoClose: 3000,
    });

    return "written";
  };

  useRegisterBillingSectionSave(saveStagedOperations);

  // Joins the encounter's navigation blocker, so leaving with billing codes the tab has not saved is
  // warned about the same way an unsaved note is.
  useEffect(() => {
    registerUnsavedCheck?.(ENCOUNTER_BILLING_TAB_SECTION, () => hasStagedChanges);

    return () => unregisterUnsavedCheck?.(ENCOUNTER_BILLING_TAB_SECTION);
  }, [hasStagedChanges, registerUnsavedCheck, unregisterUnsavedCheck]);

  return {
    stagedRows,
    hasStagedChanges,
    stageOperation,
    stagedDeletionIds,
    stageDeletion,
    unstageProcedureCode,
    previewChargeEdits,
    stageChargeEdit,
    restageProcedure,
  };
}

export default useStagedBillerSectionProcedures;
